import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { UseQueryResult } from '@tanstack/react-query';
import { BarChart3, TrendingDown, TrendingUp, type LucideIcon } from 'lucide-react';
import { CategoryLegend, CategoryPieChart } from '@/components/charts';
import { EmptyState } from '@/components/EmptyState';
import type { CategoryBreakdown, Report } from '@/lib/api';
import { ROUTES } from '@/lib/constants';
import { formatCurrency } from '@/lib/format';
import {
  ANALYSIS_BALANCE_LABEL,
  ANALYSIS_EMPTY,
  ANALYSIS_EXPENSE_HEADING,
  ANALYSIS_EXPENSE_LABEL,
  ANALYSIS_EXPENSE_NONE,
  ANALYSIS_INCOME_HEADING,
  ANALYSIS_INCOME_LABEL,
  ANALYSIS_INCOME_NONE,
  ANALYSIS_LOAD_ERROR_TITLE,
  ANALYSIS_SUMMARY_LABEL,
} from '@/lib/translations';
import { getCategoryColorHsl } from '@/lib/utils/categoryColor';
import type { ChartDataItem } from '@/types/charts';
import { Figure, FigureStrip } from './Figure';
import { ReadingError } from './ReadingError';
import { ReadingSkeleton } from './ReadingSkeleton';

// Each slice wears its category's own colour — the dot it has on Categorias and in Transações — so a category
// reads the same everywhere, and no slice borrows amber, emerald or red as an identity (DESIGN.md: those three
// are signals). The palettes are small, so two categories can share a hue: the legend's names carry identity.
const toChartData = (items: CategoryBreakdown[]): ChartDataItem[] =>
  items.map((item) => ({
    name: item.category.name,
    value: parseFloat(item.total),
    percentage: parseFloat(item.typed_percentage),
    fill: `hsl(${getCategoryColorHsl(item.category.id, item.category.type)})`,
  }));

interface BreakdownProps {
  heading: string;
  none: string;
  icon: LucideIcon;
  iconClassName: string;
  items: CategoryBreakdown[];
}

// One side of the composition — a pie and its legend, on the page itself, no card. Its heading is an h3: the
// section head above the whole reading is the h2.
const Breakdown: React.FC<BreakdownProps> = ({ heading, none, icon: Icon, iconClassName, items }) => {
  const data = toChartData(items);

  return (
    <section>
      <header className="mb-5 flex items-center gap-2">
        <Icon className={iconClassName} aria-hidden="true" />
        <h3 className="font-display text-xl font-bold tracking-tight text-foreground">{heading}</h3>
      </header>
      {data.length === 0 ? (
        <p className="text-sm text-muted-foreground">{none}</p>
      ) : (
        <div className="flex flex-col items-center gap-6 sm:flex-row">
          <CategoryPieChart data={data} />
          <CategoryLegend data={data} />
        </div>
      )}
    </section>
  );
};

export interface AnalysisReadingProps {
  reportQuery: UseQueryResult<Report>;
}

// Painel's cash-flow section (the old Análise): the period's movements — what came in, what went out, and from
// which categories. It takes any range. The totals are the API's `income`/`expense`, every movement, which is why
// they are called Entradas/Saídas and not Orçamento's Renda/Gasto (only what was classified as income or spend).
export const AnalysisReading: React.FC<AnalysisReadingProps> = ({ reportQuery }) => {
  const navigate = useNavigate();
  const { data: report } = reportQuery;

  if (reportQuery.isPending) return <ReadingSkeleton variant="analysis" />;

  if (!report) {
    return (
      <ReadingError
        title={ANALYSIS_LOAD_ERROR_TITLE}
        error={reportQuery.error}
        onRetry={() => void reportQuery.refetch()}
      />
    );
  }

  // A period with no movement has nothing to break down — say so once instead of showing zeros and two empty
  // charts. Its action is quiet (secondary): Painel's amber is already spent on "Nova Transação" above, which on a
  // phone sits a screen away from here.
  const hasAnyData = parseFloat(report.income) > 0 || parseFloat(report.expense) > 0;
  if (!hasAnyData) {
    return (
      <EmptyState
        compact
        icon={BarChart3}
        title={ANALYSIS_EMPTY.title}
        description={ANALYSIS_EMPTY.description}
        action={{ label: ANALYSIS_EMPTY.action, onClick: () => navigate(ROUTES.transactionsAdd), variant: 'secondary' }}
      />
    );
  }

  return (
    <div className="animate-fade-in space-y-8">
      <FigureStrip label={ANALYSIS_SUMMARY_LABEL}>
        <Figure label={ANALYSIS_INCOME_LABEL} anchor="income" value={formatCurrency(report.income)} />
        <Figure label={ANALYSIS_EXPENSE_LABEL} anchor="expense" value={formatCurrency(report.expense)} />
        {/* The balance is the API's `total`, only formatted; the minus sign says it is negative — no colour. */}
        <Figure
          className="col-span-2 sm:col-span-1"
          label={ANALYSIS_BALANCE_LABEL}
          value={formatCurrency(report.total)}
        />
      </FigureStrip>

      {/* grid-cols-1, not the implicit auto column: an auto track grows to its widest legend name and the page scrolls sideways. */}
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
        <Breakdown
          heading={ANALYSIS_INCOME_HEADING}
          none={ANALYSIS_INCOME_NONE}
          icon={TrendingUp}
          iconClassName="h-4 w-4 text-income"
          items={report.incomes}
        />
        <Breakdown
          heading={ANALYSIS_EXPENSE_HEADING}
          none={ANALYSIS_EXPENSE_NONE}
          icon={TrendingDown}
          iconClassName="h-4 w-4 text-expense"
          items={report.expenses}
        />
      </div>
    </div>
  );
};
